import { useCallback, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useMountEffect } from '@direct-frontend/stdlib';
import {
    type BaseRowData,
    createLoadingDataProvider,
    createStaticDataProvider,
    type DataProvider,
    withSubRowsDataProvider,
} from '@adv-frontend/data-provider';
import { type BaseQueryFn, type QueryDefinition, type QueryEndpointMethodsAndHooks } from '@adv-frontend/modules-query';
import { type InvalidateGridApiHelper } from '../../../lib/requests';
import { SEARCH_PARAM_KEY_PAGE } from '../constants';
import { type GridRow } from './types';

/**
 * Flow-based провайдер данных грида с поддержкой подстрок.
 * @typeParam GridTopLevelRow - Тип данных корневой строки грида
 * @typeParam GridSubRow - Тип данных подстроки грида
 */
export type GridDataProvider<
    GridTopLevelRow extends BaseRowData,
    GridSubRow extends BaseRowData = GridTopLevelRow,
> = DataProvider<GridTopLevelRow, DataProvider<GridSubRow>>;

/**
 * Собирает flow-based провайдер данных по текущему состоянию запроса:
 * загруженные строки — static-провайдер (подстроки подключаются вложенными провайдерами),
 * до получения данных — loading-провайдер со стабами
 * @kind helper
 * @typeParam GridTopLevelRow - Тип данных корневой строки грида
 * @typeParam GridSubRow - Тип данных подстроки грида
 * @param isFulfilled завершился ли запрос успешно
 * @param rows загруженные корневые строки грида
 * @param totalCountRows количество строк-стабов на время загрузки
 * @returns flow-based провайдер данных грида
 */
export function buildGridDataProvider<
    GridTopLevelRow extends BaseRowData & { subRows?: GridSubRow[] },
    GridSubRow extends BaseRowData = GridTopLevelRow,
>(
    isFulfilled: boolean,
    rows: GridTopLevelRow[],
    totalCountRows: number
): GridDataProvider<GridTopLevelRow, GridSubRow> {
    /**
     * Подстроки подключаются в обеих ветках, чтобы тип провайдера не зависел от состояния запроса:
     * у loading-провайдера подстрок нет по определению
     */
    if (!isFulfilled) {
        return withSubRowsDataProvider<DataProvider<GridTopLevelRow>, DataProvider<GridSubRow>>(
            createLoadingDataProvider<GridTopLevelRow>(totalCountRows),
            () => null
        );
    }

    return withSubRowsDataProvider<DataProvider<GridTopLevelRow>, DataProvider<GridSubRow>>(
        createStaticDataProvider(rows),
        (row) => {
            const { subRows } = row;

            return subRows?.length ? createStaticDataProvider(subRows) : null;
        }
    );
}

/*
 * Параметры фабрики
 */
type Options<
    Item,
    Query,
    Response,
    Tag extends string,
    GridTopLevelRow extends BaseRowData & { subRows?: GridSubRow[] } = GridRow<Item>,
    GridSubRow extends BaseRowData = GridTopLevelRow,
    ReducerPath extends string = string,
> = {
    /**
     * Эндпоинт для запроса данных
     */
    endpoint: QueryEndpointMethodsAndHooks<QueryDefinition<Query, BaseQueryFn, string | Tag, Response, ReducerPath>>;
    /**
     * Функция получения номера страницы
     * от 1
     */
    getPageNumber: (query: Query) => number;
    /**
     * Функция получения количества строк на странице
     */
    getPerPage: (query: Query) => number;
    /**
     * Функция получения строк из результата запроса
     */
    getRows: (response: Response) => GridTopLevelRow[];
    /**
     * Функция получения значения из строки
     */
    getItemFromRow: (row: GridTopLevelRow | GridSubRow) => Item | undefined;
    /**
     * Функция получения строк из результата запроса
     */
    getSelectableId?: (response: Response, rows: GridTopLevelRow[]) => string[];
    /**
     * Функция получения общего количества строк из результата запроса
     */
    getTotal: (response: Response) => number;
    /**
     * Проверяет, можно ли переиспользовать общее количество строк из предыдущего запроса.
     * Обычно возвращает `true`, когда изменился только номер страницы.
     */
    shouldUpdatePageTotal?: (newQuery: Query, lastQuery: Query) => boolean;
    /**
     * Инстанс для инвалидации данных в гриде
     * подписывается на изменения в гриде и контролирует тэги на инвалидацию в ручках
     */
    invalidateTagHelper?: InvalidateGridApiHelper<Tag, Query>;
};

type Returns<
    Item,
    Response,
    GridTopLevelRow extends BaseRowData = GridRow<Item>,
    GridSubRow extends BaseRowData = GridTopLevelRow,
> = {
    /**
     * Последний запрос вернул ошибку
     */
    isError: boolean;
    /**
     * Ошибка запроса
     */
    error: unknown;
    /**
     * Последний запрос успешен
     */
    isSuccess: boolean;
    /**
     * Последний запрос в процессе
     */
    isFetching: boolean;
    /**
     * Функция для повторного запроса
     */
    refetch: () => void;
    /**
     * Результат запроса
     */
    data: Response | undefined;
    /**
     * Общее количество строк
     */
    total: number;
    /**
     * Flow-based провайдер данных грида
     */
    gridDataProvider: GridDataProvider<GridTopLevelRow, GridSubRow>;
    /**
     * Идентификаторы строк, доступных для выбора
     */
    selectableIds: string[];
    /**
     * Функция для получения данных по идентификаторам
     */
    getDataByIds: (ids: string[]) => (Item | undefined)[];
};

const PAGE_QUERY_PATHS = [
    SEARCH_PARAM_KEY_PAGE,
    `searchParams.${SEARCH_PARAM_KEY_PAGE}`,
    `payload.${SEARCH_PARAM_KEY_PAGE}`,
];

function isSameQueryExceptPage<Query>(newQuery: Query, lastQuery: Query): boolean {
    return (
        newQuery === lastQuery ||
        isEqual(omit(newQuery as object, PAGE_QUERY_PATHS), omit(lastQuery as object, PAGE_QUERY_PATHS))
    );
}

function usePageTotal<Query, Response>(
    params: Query,
    data: Response | undefined,
    isFulfilled: boolean,
    getTotal: (response: Response) => number,
    canReuseTotalForQuery: (newQuery: Query, lastQuery: Query) => boolean
): number {
    const responseTotal = isFulfilled && data ? getTotal(data) : undefined;
    const [cachedPage, setCachedPage] = useState<{ params: Query; total: number } | undefined>(() =>
        responseTotal === undefined ? undefined : { params, total: responseTotal }
    );
    const canReuseCachedPage = cachedPage !== undefined && canReuseTotalForQuery(params, cachedPage.params);

    if (responseTotal !== undefined && (!canReuseCachedPage || cachedPage.total !== responseTotal)) {
        setCachedPage({ params, total: responseTotal });
    } else if (responseTotal === undefined && cachedPage && !canReuseCachedPage) {
        setCachedPage(undefined);
    }

    if (responseTotal !== undefined) {
        return responseTotal;
    }

    return canReuseCachedPage && cachedPage ? cachedPage.total : 0;
}

function getPageRowCount(page: number, perPage: number, total: number, isRejected: boolean): number {
    if (isRejected || total === 0) {
        return perPage;
    }

    return Math.max(0, Math.min(total - (page - 1) * perPage, perPage));
}

/**
 * Фабрика хука постраничного датапровайдера для грида.
 *
 * Сохраняет известный `total` между запросами соседних страниц, формирует строки-заглушки
 * во время загрузки и предоставляет доступ к загруженным элементам по идентификаторам.
 *
 * @typeParam Item - Доменный элемент ответа.
 * @typeParam Query - Параметры запроса endpoint-а.
 * @typeParam Response - Ответ endpoint-а.
 * @typeParam Tag - Теги инвалидации endpoint-а.
 * @typeParam GridTopLevelRow - Тип строки грида.
 * @typeParam GridSubRow - Тип подстроки грида.
 * @typeParam ReducerPath - Путь reducer-а API.
 * @returns Хук постраничного датапровайдера.
 * @kind helper
 */
export function createPaginateDataGridProvider<
    Item,
    Query,
    Response,
    Tag extends string,
    GridTopLevelRow extends BaseRowData & { subRows?: GridSubRow[] } = GridRow<Item>,
    GridSubRow extends BaseRowData = GridTopLevelRow,
    ReducerPath extends string = string,
>({
    getPageNumber,
    getPerPage,
    getTotal,
    getRows,
    getItemFromRow,
    getSelectableId = (_, rows) => rows.map((item) => item.id),
    endpoint,
    shouldUpdatePageTotal: canReuseTotalForQuery = isSameQueryExceptPage,
    invalidateTagHelper,
}: Options<Item, Query, Response, Tag, GridTopLevelRow, GridSubRow, ReducerPath>) {
    return (params: Query): Returns<Item, Response, GridTopLevelRow, GridSubRow> => {
        /** Текущая страница */
        const page = getPageNumber(params);
        /** Количество записей на странице */
        const perPage = getPerPage(params);

        /** Результат запроса и сам запрос за данными */
        const { status, error, refetch, data } = endpoint.useQuery(params);

        invalidateTagHelper?.setCurrentGridRequest(params);

        useMountEffect(() => () => {
            invalidateTagHelper?.clearCurrentGridRequest();
        });

        const rows = useMemo(() => {
            if (status === 'fulfilled' && data) {
                return getRows(data);
            }

            return [];
        }, [data, status]);

        const selectableIds = useMemo(() => {
            if (status === 'fulfilled' && data) {
                return getSelectableId(data, rows);
            }

            return [];
        }, [data, rows, status]);

        const total = usePageTotal(params, data, status === 'fulfilled', getTotal, canReuseTotalForQuery);
        const totalCountRows = useMemo(
            () => getPageRowCount(page, perPage, total, status === 'rejected'),
            [page, perPage, status, total]
        );

        const getDataByIds = useCallback(
            (ids: string[]) => {
                if (ids.length === 0) {
                    return [];
                }

                const result: (Item | undefined)[] = [];

                result.length = ids.length;

                const map = new Map<string, number>();
                const set = new Set(ids);

                ids.forEach((id, index) => {
                    map.set(id, index);
                });

                const queue: Array<GridTopLevelRow | GridSubRow> = [...rows];

                while (queue.length > 0) {
                    const row = queue.shift();

                    if (!row) {
                        continue;
                    }

                    if (set.has(row.id)) {
                        const index = map.get(row.id) ?? -1;

                        if (index >= 0) {
                            result[index] = getItemFromRow(row);
                        }
                    }

                    if ('subRows' in row && Array.isArray(row.subRows)) {
                        queue.push(...row.subRows);
                    }
                }

                return result;
            },
            [rows]
        );

        return {
            isSuccess: status === 'fulfilled',
            isFetching: status === 'pending',
            isError: status === 'rejected',
            error,
            refetch,
            data,
            total,
            gridDataProvider: useMemo(
                () => buildGridDataProvider<GridTopLevelRow, GridSubRow>(status === 'fulfilled', rows, totalCountRows),
                [rows, status, totalCountRows]
            ),
            selectableIds,
            getDataByIds,
        };
    };
}
